/**
 * Admin Bacheca ODR — gestione comunicazioni, eventi e gruppi WhatsApp.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageLayout } from "../components/PageLayout";
import {
  Megaphone, Calendar, Plus, Edit, Trash2, Users, MessageCircle, Share2,
  Filter, AlertTriangle, Sparkles, CheckCircle2, Clock, MapPin, User,
  ExternalLink, X, Loader2, Save, Send, FileText, UserCheck, UserX,
  Bell, Timer, RefreshCw, BookOpen,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PRIO_OPTS = [
  { value: "urgente", label: "Urgente", color: "bg-red-100 text-red-700 border-red-200", dot: "bg-red-500" },
  { value: "importante", label: "Importante", color: "bg-amber-100 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  { value: "info", label: "Informativo", color: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
];

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return s; }
};

const fmtDateEv = (s) => {
  if (!s) return "";
  try { const [y, m, d] = s.split("-"); return `${d}/${m}/${y}`; } catch { return s; }
};

// ============= MAIN =============
export default function AdminBachecaPage() {
  const [tab, setTab] = useState("posts"); // posts | gruppi | collaboratori | reminders | log
  const [stats, setStats] = useState(null);
  const [pendingReminders, setPendingReminders] = useState(0);

  const refreshStats = async () => {
    try {
      const r = await fetch(`${API}/bacheca/stats`);
      setStats(await r.json());
    } catch {}
    try {
      const r = await fetch(`${API}/bacheca/admin/reminders`);
      const d = await r.json();
      setPendingReminders(d.total_pending || 0);
    } catch {}
  };

  useEffect(() => { refreshStats(); }, []);

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto px-5 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
          <div>
            <p className="text-xs uppercase tracking-[3px] text-rose-500 font-semibold mb-2">Network ODR</p>
            <h1 className="text-3xl sm:text-4xl font-light text-stone-900">Bacheca <span className="italic text-rose-500">Comunicazioni</span></h1>
            <p className="text-sm text-stone-500 mt-1">Gestisci comunicazioni, eventi e gruppi WhatsApp della rete ODR.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <a
              href={`${API}/bacheca/manual/pdf`}
              target="_blank"
              rel="noreferrer"
              className="text-xs uppercase tracking-[2px] font-semibold px-4 py-2.5 rounded-full border border-stone-300 text-stone-700 hover:bg-stone-50 inline-flex items-center gap-2"
              data-testid="bacheca-manual-pdf"
            >
              <BookOpen className="w-3.5 h-3.5" /> Manuale PDF
            </a>
            <a
              href="/bacheca"
              target="_blank"
              rel="noreferrer"
              className="text-xs uppercase tracking-[2px] font-semibold px-4 py-2.5 rounded-full border border-rose-300 text-rose-600 hover:bg-rose-50 inline-flex items-center gap-2"
              data-testid="bacheca-open-public"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Apri bacheca pubblica
            </a>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-7">
            <StatCard label="Comunicazioni" value={stats.comunicazioni} icon={Megaphone} />
            <StatCard label="Eventi" value={stats.eventi} icon={Calendar} />
            <StatCard label="Eventi futuri" value={stats.eventi_futuri} icon={Clock} accent="rose" />
            <StatCard label="Urgenti" value={stats.urgenti} icon={AlertTriangle} accent="red" />
            <StatCard label="Gruppi WA" value={stats.gruppi} icon={Users} />
          </div>
        )}

        {/* Banner promemoria pendenti */}
        {pendingReminders > 0 && tab !== "reminders" && (
          <button
            onClick={() => setTab("reminders")}
            className="w-full mb-5 p-4 bg-gradient-to-r from-amber-50 to-rose-50 border border-amber-200 rounded-2xl flex items-center gap-4 hover:shadow-md transition text-left"
            data-testid="reminders-banner"
          >
            <div className="w-11 h-11 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
              <Bell className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-stone-900">
                {pendingReminders} promemoria {pendingReminders === 1 ? "pronto" : "pronti"} da inviare
              </div>
              <div className="text-xs text-stone-600">
                Eventi in programma nelle prossime 24-30 ore con gruppo WhatsApp associato.
              </div>
            </div>
            <ChevronRightArrow />
          </button>
        )}

        {/* Tabs */}
        <div className="flex gap-1 border-b border-stone-200 mb-6 overflow-x-auto">
          <TabBtn active={tab === "posts"} onClick={() => setTab("posts")} testId="tab-posts" icon={Megaphone}>Post</TabBtn>
          <TabBtn active={tab === "reminders"} onClick={() => setTab("reminders")} testId="tab-reminders" icon={Bell} badge={pendingReminders}>Promemoria</TabBtn>
          <TabBtn active={tab === "gruppi"} onClick={() => setTab("gruppi")} testId="tab-gruppi" icon={Users}>Gruppi WhatsApp</TabBtn>
          <TabBtn active={tab === "collaboratori"} onClick={() => setTab("collaboratori")} testId="tab-collaboratori" icon={User}>Collaboratori</TabBtn>
          <TabBtn active={tab === "log"} onClick={() => setTab("log")} testId="tab-log" icon={Send}>Storico invii</TabBtn>
        </div>

        {tab === "posts" && <PostsTab onChange={refreshStats} />}
        {tab === "reminders" && <RemindersTab onChange={refreshStats} />}
        {tab === "gruppi" && <GruppiTab onChange={refreshStats} />}
        {tab === "collaboratori" && <CollaboratoriTab />}
        {tab === "log" && <LogTab />}
      </div>
    </PageLayout>
  );
}

function TabBtn({ active, onClick, children, icon: Icon, testId, badge }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition border-b-2 -mb-px whitespace-nowrap ${
        active ? "border-rose-500 text-rose-600" : "border-transparent text-stone-500 hover:text-stone-800"
      }`}
    >
      <Icon className="w-4 h-4" /> {children}
      {badge > 0 && (
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${active ? "bg-rose-500 text-white" : "bg-amber-100 text-amber-700"}`}>
          {badge}
        </span>
      )}
    </button>
  );
}

function ChevronRightArrow() {
  return <svg className="w-4 h-4 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>;
}

function StatCard({ label, value, icon: Icon, accent = "stone" }) {
  const accents = {
    rose: "bg-rose-50 text-rose-600 border-rose-100",
    red: "bg-red-50 text-red-600 border-red-100",
    stone: "bg-stone-50 text-stone-600 border-stone-200",
  };
  return (
    <div className={`rounded-2xl border p-4 ${accents[accent]}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-[2px] font-semibold">{label}</span>
        <Icon className="w-4 h-4 opacity-60" />
      </div>
      <div className="text-2xl font-light text-stone-900">{value}</div>
    </div>
  );
}

// ===================== POSTS TAB =====================
function PostsTab({ onChange }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("tutti");
  const [editing, setEditing] = useState(null); // null | "new" | object
  const [sharing, setSharing] = useState(null);

  const load = async () => {
    setLoading(true);
    const params = filter !== "tutti" ? `?tipo=${filter}` : "";
    try {
      const r = await fetch(`${API}/bacheca/admin/posts${params}`);
      const d = await r.json();
      setPosts(d.posts || []);
    } catch {
      toast.error("Errore caricamento post");
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const onDelete = async (p) => {
    if (!window.confirm(`Eliminare "${p.titolo}"?`)) return;
    const r = await fetch(`${API}/bacheca/admin/posts/${p.post_id}`, { method: "DELETE" });
    if (r.ok) { toast.success("Eliminato"); load(); onChange?.(); }
    else toast.error("Errore eliminazione");
  };

  const togglePublish = async (p) => {
    const r = await fetch(`${API}/bacheca/admin/posts/${p.post_id}/publish?published=${!p.published}`, { method: "POST" });
    if (r.ok) { toast.success(p.published ? "Nascosto" : "Pubblicato"); load(); onChange?.(); }
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex gap-2">
          <FilterChip active={filter === "tutti"} onClick={() => setFilter("tutti")}>Tutti</FilterChip>
          <FilterChip active={filter === "comunicazione"} onClick={() => setFilter("comunicazione")}>Comunicazioni</FilterChip>
          <FilterChip active={filter === "evento"} onClick={() => setFilter("evento")}>Eventi</FilterChip>
        </div>
        <button
          onClick={() => setEditing("new")}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-rose-500 text-white text-xs uppercase tracking-[2px] font-semibold hover:bg-rose-600 shadow-md shadow-rose-200"
          data-testid="bacheca-new-post"
        >
          <Plus className="w-4 h-4" /> Nuovo post
        </button>
      </div>

      {loading ? (
        <div className="grid gap-2">{[1, 2, 3].map(i => <div key={i} className="h-16 rounded-xl bg-stone-100 animate-pulse" />)}</div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12 text-stone-400 text-sm">Nessun post. Crea il primo!</div>
      ) : (
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden divide-y divide-stone-100">
          {posts.map(p => (
            <div key={p.post_id} className="p-4 flex flex-wrap items-center gap-4 hover:bg-stone-50" data-testid={`post-row-${p.post_id}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className={`flex-shrink-0 w-9 h-9 rounded-xl border flex items-center justify-center ${PRIO_OPTS.find(o => o.value === p.priorita)?.color}`}>
                  {p.tipo === "evento" ? <Calendar className="w-4 h-4" /> : <Megaphone className="w-4 h-4" />}
                </span>
                <div className="min-w-0">
                  <div className="font-medium text-stone-900 truncate flex items-center gap-2">
                    {p.titolo}
                    {!p.published && <span className="text-[9px] uppercase tracking-[1.5px] bg-stone-200 text-stone-600 px-1.5 py-0.5 rounded">Bozza</span>}
                  </div>
                  <div className="text-xs text-stone-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                    <span>{p.tipo === "evento" ? "Evento" : "Comunicazione"}</span>
                    {p.regione && <span>· {p.regione}</span>}
                    {p.data_evento && <span>· {fmtDateEv(p.data_evento)}</span>}
                    <span>· {fmtDate(p.created_at)}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <IconBtn onClick={() => setSharing(p)} title="Invia su WhatsApp" testId={`share-${p.post_id}`}><MessageCircle className="w-4 h-4 text-emerald-600" /></IconBtn>
                <IconBtn onClick={() => togglePublish(p)} title={p.published ? "Nascondi" : "Pubblica"} testId={`pub-${p.post_id}`}>
                  <CheckCircle2 className={`w-4 h-4 ${p.published ? "text-emerald-600" : "text-stone-300"}`} />
                </IconBtn>
                <IconBtn onClick={() => setEditing(p)} title="Modifica" testId={`edit-${p.post_id}`}><Edit className="w-4 h-4 text-stone-600" /></IconBtn>
                <IconBtn onClick={() => onDelete(p)} title="Elimina" testId={`del-${p.post_id}`}><Trash2 className="w-4 h-4 text-red-500" /></IconBtn>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <PostEditor
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { load(); onChange?.(); }}
        />
      )}

      {sharing && (
        <SharePanel post={sharing} onClose={() => setSharing(null)} />
      )}
    </>
  );
}

function IconBtn({ children, onClick, title, testId }) {
  return (
    <button onClick={onClick} title={title} data-testid={testId} className="w-9 h-9 rounded-lg hover:bg-stone-100 flex items-center justify-center transition">{children}</button>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button onClick={onClick} className={`text-xs uppercase tracking-[1.5px] font-semibold px-3 py-1.5 rounded-full border transition ${active ? "bg-rose-500 border-rose-500 text-white shadow-md shadow-rose-200" : "bg-white border-stone-200 text-stone-600 hover:border-rose-300"}`}>
      {children}
    </button>
  );
}

// ===================== POST EDITOR =====================
function PostEditor({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(() => initial ? {
    ...initial,
    allegati: initial.allegati || [],
    reminder_gruppo_id: initial.reminder_gruppo_id || "",
  } : {
    tipo: "comunicazione",
    titolo: "",
    descrizione: "",
    priorita: "info",
    regione: "",
    citta: "",
    data_evento: "",
    ora_evento: "",
    referente: "",
    locandina_url: "",
    maps_url: "",
    allegati: [],
    autore_nome: "Admin ODR",
    autore_ruolo: "Admin ODR",
    published: true,
    reminder_gruppo_id: "",
  });
  const [regioni, setRegioni] = useState([]);
  const [gruppi, setGruppi] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/bacheca/regioni`).then(r => r.json()).then(d => setRegioni(d.regioni || [])).catch(() => {});
    fetch(`${API}/bacheca/admin/gruppi`).then(r => r.json()).then(d => setGruppi(d.gruppi || [])).catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.titolo.trim() || !form.descrizione.trim()) {
      toast.error("Titolo e descrizione obbligatori");
      return;
    }
    setSaving(true);
    const url = isEdit ? `${API}/bacheca/admin/posts/${initial.post_id}` : `${API}/bacheca/admin/posts`;
    const method = isEdit ? "PUT" : "POST";
    const r = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    setSaving(false);
    if (r.ok) { toast.success(isEdit ? "Aggiornato" : "Creato"); onSaved(); onClose(); }
    else toast.error("Errore salvataggio");
  };

  const isEvento = form.tipo === "evento";

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-2xl w-full my-8 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="post-editor">
        <div className="p-6 border-b border-stone-100 flex items-center justify-between">
          <h3 className="text-xl font-light">{isEdit ? "Modifica post" : "Nuovo post"}</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Tipo + Priorita */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tipo</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                <PillBtn active={form.tipo === "comunicazione"} onClick={() => set("tipo", "comunicazione")}>
                  <Megaphone className="w-3.5 h-3.5" /> Comunicazione
                </PillBtn>
                <PillBtn active={form.tipo === "evento"} onClick={() => set("tipo", "evento")}>
                  <Calendar className="w-3.5 h-3.5" /> Evento
                </PillBtn>
              </div>
            </div>
            <div>
              <Label>Priorità</Label>
              <div className="grid grid-cols-3 gap-2 mt-1.5">
                {PRIO_OPTS.map(p => (
                  <button key={p.value} type="button" onClick={() => set("priorita", p.value)}
                    className={`text-[10px] uppercase tracking-[1.5px] font-semibold px-2 py-2 rounded-lg border inline-flex items-center justify-center gap-1.5 ${form.priorita === p.value ? p.color : "border-stone-200 text-stone-500 bg-white hover:border-rose-300"}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${p.dot}`} /> {p.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <Field label="Titolo *">
            <input type="text" value={form.titolo} onChange={e => set("titolo", e.target.value)} className="input" data-testid="form-titolo" />
          </Field>

          <Field label="Descrizione *">
            <textarea rows={5} value={form.descrizione} onChange={e => set("descrizione", e.target.value)} className="input" data-testid="form-descrizione" />
          </Field>

          {isEvento && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Data evento"><input type="date" value={form.data_evento || ""} onChange={e => set("data_evento", e.target.value)} className="input" data-testid="form-data" /></Field>
              <Field label="Ora evento"><input type="time" value={form.ora_evento || ""} onChange={e => set("ora_evento", e.target.value)} className="input" data-testid="form-ora" /></Field>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Città"><input type="text" value={form.citta || ""} onChange={e => set("citta", e.target.value)} className="input" /></Field>
            <Field label="Regione">
              <select value={form.regione || ""} onChange={e => set("regione", e.target.value)} className="input">
                <option value="">— Nessuna —</option>
                {regioni.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
          </div>

          {isEvento && (
            <>
              <Field label="Referente"><input type="text" value={form.referente || ""} onChange={e => set("referente", e.target.value)} className="input" placeholder="es. nome - tel. 320..." /></Field>
              <Field label="Locandina (URL immagine)"><input type="url" value={form.locandina_url || ""} onChange={e => set("locandina_url", e.target.value)} className="input" placeholder="https://..." /></Field>
              <Field label="Google Maps URL"><input type="url" value={form.maps_url || ""} onChange={e => set("maps_url", e.target.value)} className="input" placeholder="https://maps.google.com/..." /></Field>
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Autore"><input type="text" value={form.autore_nome} onChange={e => set("autore_nome", e.target.value)} className="input" /></Field>
            <Field label="Ruolo"><input type="text" value={form.autore_ruolo} onChange={e => set("autore_ruolo", e.target.value)} className="input" /></Field>
          </div>

          {isEvento && (
            <Field label="🔔 Gruppo promemoria 24h prima (opzionale)">
              <select value={form.reminder_gruppo_id || ""} onChange={e => set("reminder_gruppo_id", e.target.value)} className="input" data-testid="form-reminder-gruppo">
                <option value="">— Nessun promemoria automatico —</option>
                {gruppi.map(g => <option key={g.gruppo_id} value={g.gruppo_id}>{g.nome} ({g.contatti?.length || 0} contatti)</option>)}
              </select>
              <p className="text-[11px] text-stone-400 mt-1.5">Se selezionato, il sistema mostrerà il promemoria come "pronto da inviare" nelle 24h prima dell'evento.</p>
            </Field>
          )}

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.published} onChange={e => set("published", e.target.checked)} className="w-4 h-4 accent-rose-500" />
            <span>Pubblica subito</span>
          </label>
        </div>

        <div className="p-6 border-t border-stone-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full text-stone-600 hover:bg-stone-100 text-sm font-medium">Annulla</button>
          <button onClick={save} disabled={saving} className="px-6 py-2.5 rounded-full bg-rose-500 hover:bg-rose-600 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50" data-testid="form-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isEdit ? "Salva modifiche" : "Crea post"}
          </button>
        </div>

        <style>{`
          .input{width:100%;padding:10px 14px;border:1px solid #e7e5e4;border-radius:10px;outline:none;font-size:14px;background:#fff;transition:border .2s}
          .input:focus{border-color:#fda4af;box-shadow:0 0 0 3px rgba(253,164,175,0.15)}
        `}</style>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
function Label({ children }) {
  return <label className="text-[10px] uppercase tracking-[2px] text-stone-500 font-semibold">{children}</label>;
}
function PillBtn({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick} className={`text-xs uppercase tracking-[1.5px] font-semibold px-3 py-2 rounded-lg border inline-flex items-center justify-center gap-1.5 transition ${active ? "bg-rose-500 border-rose-500 text-white" : "border-stone-200 text-stone-600 bg-white hover:border-rose-300"}`}>{children}</button>
  );
}

// ===================== SHARE PANEL (WhatsApp) =====================
function SharePanel({ post, onClose }) {
  const [gruppi, setGruppi] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customMsg, setCustomMsg] = useState("");
  const [useCustom, setUseCustom] = useState(false);

  useEffect(() => {
    fetch(`${API}/bacheca/admin/gruppi`).then(r => r.json()).then(d => {
      setGruppi(d.gruppi || []);
      if (d.gruppi && d.gruppi[0]) setSelectedId(d.gruppi[0].gruppo_id);
    });
  }, []);

  const generate = async () => {
    if (!selectedId) { toast.error("Seleziona un gruppo"); return; }
    setLoading(true);
    const r = await fetch(`${API}/bacheca/admin/posts/${post.post_id}/whatsapp`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        gruppo_id: selectedId,
        messaggio_custom: useCustom ? customMsg : null,
      }),
    });
    const d = await r.json();
    setLoading(false);
    if (!r.ok) { toast.error(d.detail || "Errore"); return; }
    setResult(d);
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-2xl w-full my-8 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="share-panel">
        <div className="p-6 border-b border-stone-100 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[2px] text-emerald-600 font-semibold">WhatsApp Share</p>
            <h3 className="text-xl font-light mt-0.5">{post.titolo}</h3>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          {!result && (
            <>
              <Field label="Gruppo destinatari">
                <select value={selectedId} onChange={e => setSelectedId(e.target.value)} className="input" data-testid="share-gruppo-select">
                  <option value="">— Seleziona —</option>
                  {gruppi.map(g => <option key={g.gruppo_id} value={g.gruppo_id}>{g.nome} ({g.contatti?.length || 0} contatti)</option>)}
                </select>
              </Field>

              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={useCustom} onChange={e => setUseCustom(e.target.checked)} className="w-4 h-4 accent-rose-500" />
                <span>Personalizza il messaggio</span>
              </label>

              {useCustom && (
                <Field label="Messaggio personalizzato">
                  <textarea rows={6} value={customMsg} onChange={e => setCustomMsg(e.target.value)} className="input" placeholder="Lascia vuoto per usare il template automatico..." />
                </Field>
              )}

              <button onClick={generate} disabled={loading || !selectedId}
                className="w-full py-3 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm uppercase tracking-[2px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="share-generate">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                Genera link WhatsApp
              </button>

              <p className="text-xs text-stone-500 text-center">
                Verranno generati link <strong>wa.me</strong> precompilati: cliccaci sopra per inviare il messaggio da WhatsApp Web o mobile.
              </p>
            </>
          )}

          {result && (
            <>
              <div className="bg-stone-50 rounded-2xl p-4 border border-stone-200">
                <div className="text-[10px] uppercase tracking-[2px] text-stone-500 font-semibold mb-2">Messaggio</div>
                <pre className="text-sm text-stone-700 whitespace-pre-wrap font-sans leading-relaxed">{result.messaggio}</pre>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">{result.gruppo_nome}</div>
                  <div className="text-xs text-stone-500">{result.totale} destinatari</div>
                </div>
                <button onClick={() => { result.links.forEach((l, i) => setTimeout(() => window.open(l.url, "_blank"), i * 200)); }}
                  className="text-xs uppercase tracking-[1.5px] font-semibold px-3 py-2 rounded-full border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  data-testid="share-open-all"
                >
                  Apri tutti
                </button>
              </div>

              <div className="max-h-96 overflow-y-auto border border-stone-200 rounded-2xl divide-y divide-stone-100">
                {result.links.length === 0 ? (
                  <div className="p-6 text-center text-sm text-stone-400">Nessun contatto valido nel gruppo.</div>
                ) : result.links.map((l, i) => (
                  <a key={i} href={l.url} target="_blank" rel="noreferrer"
                    className="flex items-center justify-between gap-3 p-3 hover:bg-stone-50"
                    data-testid={`share-link-${i}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-stone-900 truncate">{l.nome || l.telefono}</div>
                      <div className="text-xs text-stone-500">+{l.telefono}{l.regione && ` · ${l.regione}`}</div>
                    </div>
                    <MessageCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  </a>
                ))}
              </div>

              <button onClick={() => setResult(null)} className="w-full py-2.5 rounded-full border border-stone-200 text-stone-600 text-sm font-medium hover:bg-stone-50">Cambia gruppo</button>
            </>
          )}
        </div>

        <style>{`
          .input{width:100%;padding:10px 14px;border:1px solid #e7e5e4;border-radius:10px;outline:none;font-size:14px;background:#fff}
          .input:focus{border-color:#fda4af;box-shadow:0 0 0 3px rgba(253,164,175,0.15)}
        `}</style>
      </div>
    </div>
  );
}

// ===================== GRUPPI TAB =====================
function GruppiTab({ onChange }) {
  const [gruppi, setGruppi] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const r = await fetch(`${API}/bacheca/admin/gruppi`);
    const d = await r.json();
    setGruppi(d.gruppi || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const onDelete = async (g) => {
    if (!window.confirm(`Eliminare il gruppo "${g.nome}"?`)) return;
    const r = await fetch(`${API}/bacheca/admin/gruppi/${g.gruppo_id}`, { method: "DELETE" });
    if (r.ok) { toast.success("Eliminato"); load(); onChange?.(); }
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <button onClick={() => setEditing("new")} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-rose-500 text-white text-xs uppercase tracking-[2px] font-semibold hover:bg-rose-600 shadow-md shadow-rose-200" data-testid="gruppo-new">
          <Plus className="w-4 h-4" /> Nuovo gruppo
        </button>
      </div>

      {loading ? (
        <div className="grid gap-2">{[1, 2].map(i => <div key={i} className="h-16 rounded-xl bg-stone-100 animate-pulse" />)}</div>
      ) : gruppi.length === 0 ? (
        <div className="text-center py-12 text-stone-400">
          <Users className="w-10 h-10 mx-auto mb-3 text-stone-300" />
          <p className="text-sm">Nessun gruppo. Crea il primo per inviare WhatsApp segmentati.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {gruppi.map(g => (
            <div key={g.gruppo_id} className="bg-white rounded-2xl border border-stone-200 p-5" data-testid={`gruppo-card-${g.gruppo_id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <div className="font-medium text-stone-900 truncate">{g.nome}</div>
                  {g.descrizione && <div className="text-xs text-stone-500 mt-0.5">{g.descrizione}</div>}
                </div>
                <div className="flex gap-1">
                  <IconBtn onClick={() => setEditing(g)} title="Modifica"><Edit className="w-4 h-4 text-stone-600" /></IconBtn>
                  <IconBtn onClick={() => onDelete(g)} title="Elimina"><Trash2 className="w-4 h-4 text-red-500" /></IconBtn>
                </div>
              </div>
              <div className="text-xs text-stone-500 flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" /> {g.contatti?.length || 0} contatti
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <GruppoEditor initial={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { load(); onChange?.(); }} />}
    </>
  );
}

function GruppoEditor({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [nome, setNome] = useState(initial?.nome || "");
  const [descrizione, setDescrizione] = useState(initial?.descrizione || "");
  const [contatti, setContatti] = useState(initial?.contatti || [{ nome: "", telefono: "", regione: "", ruolo: "" }]);
  const [saving, setSaving] = useState(false);

  const updateContatto = (i, k, v) => setContatti(cs => cs.map((c, idx) => idx === i ? { ...c, [k]: v } : c));
  const addContatto = () => setContatti(cs => [...cs, { nome: "", telefono: "", regione: "", ruolo: "" }]);
  const delContatto = (i) => setContatti(cs => cs.filter((_, idx) => idx !== i));

  const bulkPaste = () => {
    const txt = prompt("Incolla righe nel formato: Nome | Telefono | Regione | Ruolo (una per riga). Accetta anche separatore TAB o virgola.");
    if (!txt) return;
    const rows = txt.split("\n").map(r => r.split(/\t|\||,/).map(s => s.trim())).filter(r => r.length >= 2 && r[1]);
    setContatti(cs => [...cs.filter(c => c.telefono), ...rows.map(([n, t, r, ru]) => ({ nome: n || "", telefono: t || "", regione: r || "", ruolo: ru || "" }))]);
    toast.success(`Aggiunti ${rows.length} contatti`);
  };

  const save = async () => {
    if (!nome.trim()) { toast.error("Nome gruppo obbligatorio"); return; }
    const validContacts = contatti.filter(c => c.telefono && c.telefono.trim());
    setSaving(true);
    const url = isEdit ? `${API}/bacheca/admin/gruppi/${initial.gruppo_id}` : `${API}/bacheca/admin/gruppi`;
    const method = isEdit ? "PUT" : "POST";
    const r = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ nome, descrizione, contatti: validContacts }),
    });
    setSaving(false);
    if (r.ok) { toast.success(isEdit ? "Aggiornato" : "Creato"); onSaved(); onClose(); }
    else toast.error("Errore salvataggio");
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-3xl w-full my-8 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="gruppo-editor">
        <div className="p-6 border-b border-stone-100 flex items-center justify-between">
          <h3 className="text-xl font-light">{isEdit ? "Modifica gruppo" : "Nuovo gruppo"}</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          <Field label="Nome gruppo *"><input type="text" value={nome} onChange={e => setNome(e.target.value)} className="input" data-testid="gruppo-nome" placeholder="es. Agenti Sud Italia" /></Field>
          <Field label="Descrizione"><input type="text" value={descrizione} onChange={e => setDescrizione(e.target.value)} className="input" placeholder="es. Distributori Lombardia, Team commerciale..." /></Field>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Contatti ({contatti.filter(c => c.telefono).length})</Label>
              <div className="flex gap-2">
                <button onClick={bulkPaste} className="text-xs text-rose-600 hover:text-rose-700 font-semibold uppercase tracking-[1.5px]" data-testid="gruppo-bulk">Incolla in massa</button>
                <button onClick={addContatto} className="text-xs text-rose-600 hover:text-rose-700 font-semibold uppercase tracking-[1.5px] inline-flex items-center gap-1" data-testid="gruppo-add-contatto"><Plus className="w-3 h-3" />Aggiungi</button>
              </div>
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto border border-stone-200 rounded-2xl p-3 bg-stone-50">
              {contatti.map((c, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <input value={c.nome} onChange={e => updateContatto(i, "nome", e.target.value)} placeholder="Nome" className="input col-span-3" data-testid={`contatto-nome-${i}`} />
                  <input value={c.telefono} onChange={e => updateContatto(i, "telefono", e.target.value)} placeholder="+39320..." className="input col-span-3" data-testid={`contatto-tel-${i}`} />
                  <input value={c.regione} onChange={e => updateContatto(i, "regione", e.target.value)} placeholder="Regione" className="input col-span-3" />
                  <input value={c.ruolo} onChange={e => updateContatto(i, "ruolo", e.target.value)} placeholder="Ruolo" className="input col-span-2" />
                  <button onClick={() => delContatto(i)} className="col-span-1 text-red-500 hover:text-red-700 flex justify-center"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-stone-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full text-stone-600 hover:bg-stone-100 text-sm font-medium">Annulla</button>
          <button onClick={save} disabled={saving} className="px-6 py-2.5 rounded-full bg-rose-500 hover:bg-rose-600 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50" data-testid="gruppo-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salva
          </button>
        </div>

        <style>{`
          .input{width:100%;padding:9px 12px;border:1px solid #e7e5e4;border-radius:8px;outline:none;font-size:13px;background:#fff}
          .input:focus{border-color:#fda4af;box-shadow:0 0 0 3px rgba(253,164,175,0.15)}
        `}</style>
      </div>
    </div>
  );
}

// ===================== COLLABORATORI TAB =====================
function CollaboratoriTab() {
  const [collaboratori, setCollaboratori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | object

  const load = async () => {
    setLoading(true);
    const r = await fetch(`${API}/bacheca/admin/collaboratori`);
    const d = await r.json();
    setCollaboratori(d.collaboratori || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggleAttivo = async (c) => {
    const r = await fetch(`${API}/bacheca/admin/collaboratori/${c.collaboratore_id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attivo: !c.attivo }),
    });
    if (r.ok) { toast.success(c.attivo ? "Disattivato" : "Attivato"); load(); }
  };

  const onDelete = async (c) => {
    if (!window.confirm(`Eliminare "${c.nome}"?`)) return;
    const r = await fetch(`${API}/bacheca/admin/collaboratori/${c.collaboratore_id}`, { method: "DELETE" });
    if (r.ok) { toast.success("Eliminato"); load(); }
  };

  const seedDefault = async () => {
    if (!window.confirm("Inserire la lista predefinita di collaboratori ODR?")) return;
    const r = await fetch(`${API}/bacheca/admin/collaboratori/seed-default`, { method: "POST" });
    if (r.ok) { const d = await r.json(); toast.success(`Aggiunti ${d.inserted}, già presenti ${d.skipped}`); load(); }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="text-sm text-stone-500">
          Chi è in questa lista può <strong className="text-stone-700">pubblicare in bacheca</strong> dal frontend pubblico.
        </div>
        <div className="flex gap-2">
          {collaboratori.length === 0 && (
            <button onClick={seedDefault} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-stone-100 text-stone-600 text-xs uppercase tracking-[2px] font-semibold hover:bg-stone-200" data-testid="collab-seed">
              <Sparkles className="w-4 h-4" /> Carica lista default
            </button>
          )}
          <button onClick={() => setEditing("new")} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-rose-500 text-white text-xs uppercase tracking-[2px] font-semibold hover:bg-rose-600 shadow-md shadow-rose-200" data-testid="collab-new">
            <Plus className="w-4 h-4" /> Nuovo collaboratore
          </button>
        </div>
      </div>

      {loading ? (
        <div className="grid gap-2">{[1, 2, 3].map(i => <div key={i} className="h-14 rounded-xl bg-stone-100 animate-pulse" />)}</div>
      ) : collaboratori.length === 0 ? (
        <div className="text-center py-12 text-stone-400">
          <User className="w-10 h-10 mx-auto mb-3 text-stone-300" />
          <p className="text-sm">Nessun collaboratore. Carica la lista predefinita o aggiungili manualmente.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden divide-y divide-stone-100">
          {collaboratori.map(c => (
            <div key={c.collaboratore_id} className="p-4 flex items-center gap-4" data-testid={`collab-row-${c.collaboratore_id}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0 ${c.attivo ? "bg-rose-100 text-rose-700" : "bg-stone-100 text-stone-400"}`}>
                {c.nome.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`font-medium ${c.attivo ? "text-stone-900" : "text-stone-400"}`}>{c.nome}</span>
                  {!c.attivo && <span className="text-[9px] uppercase tracking-[1.5px] bg-stone-200 text-stone-600 px-1.5 py-0.5 rounded">Inattivo</span>}
                </div>
                <div className="text-xs text-stone-500 truncate">{c.ruolo}{c.regione ? ` · ${c.regione}` : ""}</div>
              </div>
              <div className="flex items-center gap-1.5">
                <IconBtn onClick={() => toggleAttivo(c)} title={c.attivo ? "Disattiva" : "Attiva"} testId={`collab-toggle-${c.collaboratore_id}`}>
                  {c.attivo ? <UserCheck className="w-4 h-4 text-emerald-600" /> : <UserX className="w-4 h-4 text-stone-400" />}
                </IconBtn>
                <IconBtn onClick={() => setEditing(c)} title="Modifica" testId={`collab-edit-${c.collaboratore_id}`}><Edit className="w-4 h-4 text-stone-600" /></IconBtn>
                <IconBtn onClick={() => onDelete(c)} title="Elimina" testId={`collab-del-${c.collaboratore_id}`}><Trash2 className="w-4 h-4 text-red-500" /></IconBtn>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <CollaboratoreEditor
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { load(); }}
        />
      )}
    </>
  );
}

function CollaboratoreEditor({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    nome: initial?.nome || "",
    ruolo: initial?.ruolo || "",
    regione: initial?.regione || "",
    email: initial?.email || "",
    attivo: initial?.attivo !== false,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.nome.trim() || !form.ruolo.trim()) {
      toast.error("Nome e ruolo obbligatori");
      return;
    }
    setSaving(true);
    const url = isEdit ? `${API}/bacheca/admin/collaboratori/${initial.collaboratore_id}` : `${API}/bacheca/admin/collaboratori`;
    const method = isEdit ? "PUT" : "POST";
    const r = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    setSaving(false);
    if (r.ok) { toast.success(isEdit ? "Aggiornato" : "Aggiunto"); onSaved(); onClose(); }
    else toast.error("Errore salvataggio");
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-md w-full my-8 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="collab-editor">
        <div className="p-6 border-b border-stone-100 flex items-center justify-between">
          <h3 className="text-xl font-light">{isEdit ? "Modifica collaboratore" : "Nuovo collaboratore"}</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <Field label="Nome e cognome *">
            <input type="text" value={form.nome} onChange={e => set("nome", e.target.value)} className="input" data-testid="collab-form-nome" placeholder="es. Emanuel Cirenei" />
          </Field>
          <Field label="Ruolo *">
            <input type="text" value={form.ruolo} onChange={e => set("ruolo", e.target.value)} className="input" data-testid="collab-form-ruolo" placeholder="es. Direttore Commerciale" />
          </Field>
          <Field label="Regione (opzionale)">
            <input type="text" value={form.regione} onChange={e => set("regione", e.target.value)} className="input" placeholder="es. Lombardia" />
          </Field>
          <Field label="Email (opzionale)">
            <input type="email" value={form.email} onChange={e => set("email", e.target.value)} className="input" />
          </Field>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.attivo} onChange={e => set("attivo", e.target.checked)} className="w-4 h-4 accent-rose-500" />
            <span>Attivo (può pubblicare in bacheca)</span>
          </label>
        </div>
        <div className="p-6 border-t border-stone-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full text-stone-600 hover:bg-stone-100 text-sm font-medium">Annulla</button>
          <button onClick={save} disabled={saving} className="px-6 py-2.5 rounded-full bg-rose-500 hover:bg-rose-600 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50" data-testid="collab-form-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salva
          </button>
        </div>
        <style>{`
          .input{width:100%;padding:10px 14px;border:1px solid #e7e5e4;border-radius:10px;outline:none;font-size:14px;background:#fff}
          .input:focus{border-color:#fda4af;box-shadow:0 0 0 3px rgba(253,164,175,0.15)}
        `}</style>
      </div>
    </div>
  );
}

// ===================== REMINDERS TAB =====================
const fmtHours = (h) => {
  if (h === null || h === undefined) return "—";
  if (h < 0) return "passato";
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 24) return `${Math.round(h)}h`;
  return `${Math.round(h / 24)} giorni`;
};

function RemindersTab({ onChange }) {
  const [data, setData] = useState({ pending_now: [], upcoming: [], sent: [] });
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/bacheca/admin/reminders`);
      setData(await r.json());
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const markSent = async (post) => {
    const r = await fetch(`${API}/bacheca/admin/reminders/${post.post_id}/mark-sent`, { method: "POST" });
    if (r.ok) { toast.success("Promemoria archiviato"); load(); onChange?.(); }
  };

  const reset = async (post) => {
    if (!window.confirm("Riportare il promemoria allo stato 'da inviare'?")) return;
    const r = await fetch(`${API}/bacheca/admin/reminders/${post.post_id}/reset`, { method: "POST" });
    if (r.ok) { toast.success("Reimpostato"); load(); onChange?.(); }
  };

  if (loading) return <div className="grid gap-2">{[1, 2, 3].map(i => <div key={i} className="h-20 rounded-xl bg-stone-100 animate-pulse" />)}</div>;

  return (
    <div className="space-y-7">
      {/* Pending now */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Bell className="w-4 h-4 text-amber-600" />
          <h3 className="text-sm uppercase tracking-[2px] font-semibold text-stone-700">
            Da inviare ora <span className="text-amber-600">({data.pending_now.length})</span>
          </h3>
        </div>
        {data.pending_now.length === 0 ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-center text-sm text-emerald-700" data-testid="reminders-empty-pending">
            <CheckCircle2 className="w-6 h-6 mx-auto mb-1.5 text-emerald-500" />
            Nessun promemoria da inviare ora. Tutto sotto controllo.
          </div>
        ) : (
          <div className="grid gap-3" data-testid="reminders-pending-list">
            {data.pending_now.map(ev => (
              <ReminderRow key={ev.post_id} ev={ev} onSend={() => setSharing(ev)} onMarkSent={() => markSent(ev)} highlight />
            ))}
          </div>
        )}
      </section>

      {/* Upcoming */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Timer className="w-4 h-4 text-stone-500" />
          <h3 className="text-sm uppercase tracking-[2px] font-semibold text-stone-700">
            Prossimi eventi con promemoria <span className="text-stone-400">({data.upcoming.length})</span>
          </h3>
        </div>
        {data.upcoming.length === 0 ? (
          <div className="text-sm text-stone-400 text-center py-6">
            Nessun evento futuro con promemoria configurato.<br />
            <span className="text-[11px]">Assegna un "Gruppo promemoria" agli eventi nella tab Post.</span>
          </div>
        ) : (
          <div className="grid gap-2">
            {data.upcoming.map(ev => <ReminderRow key={ev.post_id} ev={ev} onSend={() => setSharing(ev)} compact />)}
          </div>
        )}
      </section>

      {/* Sent */}
      {data.sent.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <h3 className="text-sm uppercase tracking-[2px] font-semibold text-stone-700">
              Già inviati <span className="text-emerald-600">({data.sent.length})</span>
            </h3>
          </div>
          <div className="grid gap-2">
            {data.sent.map(ev => <ReminderRow key={ev.post_id} ev={ev} onReset={() => reset(ev)} sent />)}
          </div>
        </section>
      )}

      {sharing && (
        <ReminderSharePanel
          ev={sharing}
          onClose={() => setSharing(null)}
          onSent={() => { markSent(sharing); setSharing(null); }}
        />
      )}
    </div>
  );
}

function ReminderRow({ ev, onSend, onMarkSent, onReset, highlight, compact, sent }) {
  return (
    <div className={`flex items-center gap-3 p-4 rounded-2xl border ${highlight ? "bg-amber-50 border-amber-200" : sent ? "bg-stone-50 border-stone-200" : "bg-white border-stone-200"}`} data-testid={`reminder-row-${ev.post_id}`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${highlight ? "bg-amber-100 text-amber-700" : sent ? "bg-emerald-100 text-emerald-700" : "bg-rose-50 text-rose-600"}`}>
        {sent ? <CheckCircle2 className="w-4 h-4" /> : highlight ? <Bell className="w-4 h-4" /> : <Calendar className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-stone-900 truncate">{ev.titolo}</div>
        <div className="text-xs text-stone-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> tra {fmtHours(ev.hours_until)}</span>
          {ev.data_evento && <span>{ev.data_evento.split("-").reverse().join("/")}{ev.ora_evento ? ` ${ev.ora_evento}` : ""}</span>}
          {ev.gruppo && <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {ev.gruppo.nome} ({ev.gruppo.contatti_count})</span>}
        </div>
      </div>
      {!compact && !sent && (
        <button onClick={onSend} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs uppercase tracking-[1.5px] font-semibold shadow-md shadow-emerald-200" data-testid={`reminder-send-${ev.post_id}`}>
          <MessageCircle className="w-3.5 h-3.5" /> Invia
        </button>
      )}
      {compact && !sent && (
        <button onClick={onSend} className="text-xs uppercase tracking-[1.5px] font-semibold text-rose-600 hover:text-rose-700 inline-flex items-center gap-1">
          <Send className="w-3 h-3" /> Anticipa
        </button>
      )}
      {sent && onReset && (
        <button onClick={onReset} className="text-xs uppercase tracking-[1.5px] font-semibold text-stone-500 hover:text-rose-600 inline-flex items-center gap-1" data-testid={`reminder-reset-${ev.post_id}`}>
          <RefreshCw className="w-3 h-3" /> Reset
        </button>
      )}
    </div>
  );
}

function ReminderSharePanel({ ev, onClose, onSent }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/bacheca/admin/reminders/${ev.post_id}/whatsapp`, { method: "POST" })
      .then(r => r.json())
      .then(d => { setResult(d); setLoading(false); })
      .catch(() => { setLoading(false); toast.error("Errore generazione"); });
  }, [ev.post_id]);

  const openAll = () => {
    if (!result?.links) return;
    result.links.forEach((l, i) => setTimeout(() => window.open(l.url, "_blank"), i * 300));
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-2xl w-full my-8 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="reminder-share-panel">
        <div className="p-6 border-b border-stone-100 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[2px] text-emerald-600 font-semibold">Promemoria 24h</p>
            <h3 className="text-xl font-light mt-0.5">{ev.titolo}</h3>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          {loading ? (
            <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-rose-500 mx-auto" /></div>
          ) : (
            <>
              <div className="bg-stone-50 rounded-2xl p-4 border border-stone-200">
                <div className="text-[10px] uppercase tracking-[2px] text-stone-500 font-semibold mb-2">Messaggio</div>
                <pre className="text-sm text-stone-700 whitespace-pre-wrap font-sans leading-relaxed">{result?.messaggio}</pre>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">{result?.gruppo_nome}</div>
                  <div className="text-xs text-stone-500">{result?.totale} destinatari</div>
                </div>
                <button onClick={openAll} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs uppercase tracking-[1.5px] font-semibold" data-testid="reminder-open-all">
                  <MessageCircle className="w-3.5 h-3.5" /> Apri tutti
                </button>
              </div>

              <div className="max-h-80 overflow-y-auto border border-stone-200 rounded-2xl divide-y divide-stone-100">
                {result?.links?.length === 0 ? (
                  <div className="p-6 text-center text-sm text-stone-400">Nessun contatto valido.</div>
                ) : result?.links?.map((l, i) => (
                  <a key={i} href={l.url} target="_blank" rel="noreferrer" className="flex items-center justify-between p-3 hover:bg-stone-50">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-stone-900 truncate">{l.nome || l.telefono}</div>
                      <div className="text-xs text-stone-500">+{l.telefono}</div>
                    </div>
                    <MessageCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  </a>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="p-6 border-t border-stone-100 flex flex-col sm:flex-row justify-end gap-2">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full text-stone-600 hover:bg-stone-100 text-sm font-medium">Chiudi</button>
          <button onClick={onSent} className="px-6 py-2.5 rounded-full bg-rose-500 hover:bg-rose-600 text-white text-sm font-medium inline-flex items-center justify-center gap-2 shadow-md shadow-rose-200" data-testid="reminder-mark-sent">
            <CheckCircle2 className="w-4 h-4" /> Segna come inviato
          </button>
        </div>
      </div>
    </div>
  );
}

// ===================== LOG TAB =====================
function LogTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/bacheca/share-log`).then(r => r.json()).then(d => {
      setLogs(d.logs || []);
      setLoading(false);
    });
  }, []);

  if (loading) return <div className="grid gap-2">{[1, 2, 3].map(i => <div key={i} className="h-14 rounded-xl bg-stone-100 animate-pulse" />)}</div>;
  if (logs.length === 0) return <div className="text-center py-12 text-stone-400 text-sm">Nessun invio registrato.</div>;

  return (
    <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden divide-y divide-stone-100">
      {logs.map(l => (
        <div key={l.log_id} className="p-4 flex items-center gap-4">
          <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center"><Send className="w-4 h-4" /></div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-stone-900 truncate">{l.post_titolo}</div>
            <div className="text-xs text-stone-500">{l.gruppo_nome} · {l.destinatari} destinatari</div>
          </div>
          <div className="text-xs text-stone-400">{fmtDate(l.created_at)}</div>
        </div>
      ))}
    </div>
  );
}
